import type { PaymentPlanOption } from "./types";

export const WINBACK_PROMO_CODE = "PARABLES_YEARLY_1999";
export const FIRST_WEEK_PROMO_CODE = "PARABLES_FIRST_WEEK";
export type PaywallExitReason = "price" | "usage" | "information";
export const PAYWALL_EXIT_OPTIONS: { id: PaywallExitReason; label: string }[] = [
  { id: "usage", label: "I'm not sure if I'll use it." },
  { id: "information", label: "I don't know what I'm getting." },
  { id: "price", label: "The price" },
];
export type PaywallOfferState = { visible: boolean; reason?: PaywallExitReason };

export function isWinbackPromoCode(code?: string): boolean {
  return [WINBACK_PROMO_CODE, "PARABLES_YEARLY_2999", "PARABLES_YEARLY_SPECIAL", "DOUBLE_GRACE_15028"].includes(code?.toUpperCase() ?? "");
}

export function commitPaywallExitReason(_state: PaywallOfferState | null, reason: PaywallExitReason): PaywallOfferState {
  return { visible: true, reason };
}

export function readPaywallOffer(raw: string | null): PaywallOfferState | null {
  try {
    const state = JSON.parse(raw ?? "null");
    if (!state || typeof state.visible !== "boolean") return null;
    // The old commitment branch has been retired. Ask again instead of showing
    // an unrelated offer to someone who selected it in a previous build.
    if (state.reason === "commitment") return { visible: false };
    return { visible: state.visible, ...(PAYWALL_EXIT_OPTIONS.some(option => option.id === state.reason) ? { reason: state.reason } : {}) };
  } catch { return null; }
}

export function billingPlanId(plan: PaymentPlanOption): string {
  return plan.offer?.planId ?? plan.id;
}

export function isVerifiedIntroOffer(plan: PaymentPlanOption, code: string): boolean {
  if (code !== FIRST_WEEK_PROMO_CODE && code !== WINBACK_PROMO_CODE) return false;
  const expectedPlan = code === FIRST_WEEK_PROMO_CODE ? "weekly" : "annual";
  const expectedAmount = code === FIRST_WEEK_PROMO_CODE ? 300 : 1999;
  return plan.offer?.promoCode === code && plan.offer.planId === expectedPlan &&
    plan.offer.firstPeriodAmount === expectedAmount && plan.offer.discountDuration === (expectedPlan === "weekly" ? "once" : "forever") &&
    plan.currencyCode === "USD" && plan.minorUnitDivisor === 100 &&
    Number.isFinite(plan.unitAmount) && (plan.unitAmount ?? 0) > expectedAmount;
}

export function getPaywallPlans(plans: PaymentPlanOption[], offer: boolean, reason: PaywallExitReason = "price"): PaymentPlanOption[] {
  if (offer) {
    if (reason === "information") return [];
    const code = reason === "usage" ? FIRST_WEEK_PROMO_CODE : WINBACK_PROMO_CODE;
    return plans.filter(plan => isVerifiedIntroOffer(plan, code));
  }
  const regular = plans.filter(plan => !plan.offer);
  const shorter = regular.find(plan => plan.id === "monthly");
  return [shorter, regular.find(plan => plan.id === "annual")].filter((plan): plan is PaymentPlanOption => Boolean(plan));
}

export function planAmount(plan: PaymentPlanOption, discounted = false): number {
  if (discounted && !isVerifiedIntroOffer(plan, plan.offer?.promoCode ?? ""))
    throw new Error("No verified introductory offer is available for this plan.");
  return (discounted ? plan.offer!.firstPeriodAmount : plan.unitAmount ?? 0) / (plan.minorUnitDivisor ?? 100);
}

export function formatPlanPrice(plan: PaymentPlanOption, discounted = false): string {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: plan.currencyCode ?? "USD" }).format(planAmount(plan, discounted));
}

export function planPeriod(plan: PaymentPlanOption): string {
  return billingPlanId(plan) === "annual" ? "year" : billingPlanId(plan) === "weekly" ? "week" : "month";
}

export function renewalAmount(plan: PaymentPlanOption, discounted = false): number {
  return planAmount(plan, discounted && plan.offer?.discountDuration === "forever");
}
